import { CognitoUser, CognitoUserAttribute } from 'amazon-cognito-identity-js';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useCatchError } from 'src/hooks/use-catch-error';
import { assertContext } from 'src/utils/assert-context';
import * as cognito from 'src/utils/cognito';
import { Unauthorized } from 'src/utils/errors';
import { isObject } from 'src/utils/is-object';
import { useMemoOne } from 'use-memo-one';

export type Cognito = typeof cognito;

const CognitoContext = createContext(cognito);

// just for mocking
export const CognitoProvider = CognitoContext.Provider;

export function useCognito() {
  return useContext(CognitoContext);
}

type CognitoUserContextType = {
  data: CognitoUser | null;
  refreshData: () => void;
};

// pass undefined as any, because we run assertContext at runtime
const CognitoUserContext = createContext<CognitoUserContextType>(
  undefined as any
);

export function useUserAttributes() {
  const catchError = useCatchError();
  const { getSession } = useCognito();
  const { data: user } = useCognitoUser();

  const [loading, setLoading] = useState(false);
  const [userAttributes, setUserAttributes] = useState<CognitoUserAttribute[]>(
    []
  );

  useEffect(() => {
    if (!user) return;

    const getUserData = async () => {
      setLoading(true);

      try {
        const session = await getSession(user);

        if (!session.isValid()) {
          throw new Unauthorized();
        }

        user.getUserAttributes((_err, result) => {
          setUserAttributes(result || []);
          setLoading(false);
        });
      } catch (error) {
        if (isObject(error) && error.code === 'NotAuthorizedException') {
          catchError(new Unauthorized());
        } else {
          catchError(error);
        }
      }
    };

    getUserData();
  }, [user, getSession, catchError]);

  return { user, loading, userAttributes };
}

export const useCognitoUser = () => {
  const context = useContext(CognitoUserContext);
  assertContext(context, 'CognitoUser');
  return context;
};

export const CognitoUserProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { getCurrentUser } = useCognito();
  const [data, setUser] = useState(getCurrentUser());

  const refreshData = useCallback(() => {
    setUser(getCurrentUser());
  }, [setUser, getCurrentUser]);

  const value = useMemoOne<CognitoUserContextType>(
    () => ({ data, refreshData }),
    [data, refreshData]
  );

  return (
    <CognitoUserContext.Provider value={value}>
      {children}
    </CognitoUserContext.Provider>
  );
};
